/**
 * TenantProvider — central tenant scope attachment.
 *
 * Two independent paths resolve to the active tenant:
 *
 *   1. Auth path: a signed-in `store_manager` automatically attaches to
 *      the tenant they own (via `useTenantAutoAttach`).
 *   2. Host path: any visitor (signed in or not) on
 *      `<slug>.khaysdevices.store` (or the `/s/<slug>` path fallback) is
 *      attached to that tenant for read-only storefront browsing.
 *
 * The auth path takes priority. If a store manager signs in while on a
 * subdomain that doesn't match the tenant they own, the auth path wins
 * (their owned tenant scope is used).
 *
 * While we're still resolving, the admin tree is held back so a Store
 * Manager never sees a flash of main-site data before their tenant scope
 * is applied.
 */
import { ReactNode, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  useTenantAutoAttach,
  useTenantStore,
  type TenantInfo,
} from "@/lib/tenantContext";
import { useAuth } from "@/hooks/useAuth";
import ManagedByPlatformBanner from "@/components/ManagedByPlatformBanner";
import { resolveTenantFromLocation } from "@/lib/tenantHost";
import { supabase } from "@/integrations/supabase/client";
import { setCloudSyncTenantScope, loadFromCloud } from "@/data/cloudSync";

function useTenantHostAttach(): { resolving: boolean } {
  const { user, profile } = useAuth();
  const tenantId = useTenantStore(s => s.tenantId);
  const setScope = useTenantStore(s => s.setScope);
  const [resolving, setResolving] = useState(() => !!resolveTenantFromLocation().slug);

  useEffect(() => {
    // Auth path always wins for store managers — skip host resolution.
    if (profile?.role === "store_manager") { setResolving(false); return; }
    // If another path already attached a tenant, leave it alone.
    if (tenantId) { setResolving(false); return; }

    const { slug } = resolveTenantFromLocation();
    if (!slug) { setResolving(false); return; }
    let cancelled = false;
    setResolving(true);
    (async () => {
      try {
        const { data, error } = await supabase.rpc("get_tenant_by_slug" as any, { _slug: slug });
        if (cancelled) return;
        const row = Array.isArray(data) ? data[0] : data;
        if (!error && row) {
          const tenant: TenantInfo = {
            id: row.id,
            slug: row.slug,
            store_title: row.store_title,
            hero_heading: row.hero_heading ?? null,
            description: row.description ?? null,
            logo_url: row.logo_url ?? null,
            banner_url: row.banner_url ?? null,
            status: row.status ?? "active",
            business_categories: row.business_categories ?? null,
          };
          await setScope(tenant, null);
        } else {
          // Unknown slug — fall through to main store.
          setCloudSyncTenantScope(null);
          try { await loadFromCloud(); } catch {}
        }
      } catch {
        // Network failure → main store fallback.
        setCloudSyncTenantScope(null);
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
    // We only re-resolve on auth state change or first mount.
  }, [user?.id, profile?.role, tenantId, setScope]);

  return { resolving };
}

export default function TenantProvider({ children }: { children: ReactNode }) {
  useTenantAutoAttach();
  const hostAttach = useTenantHostAttach();
  const { user, profile, loading } = useAuth();
  const resolving = useTenantStore(s => s.resolving) || hostAttach.resolving;
  const tenantId = useTenantStore(s => s.tenantId);

  const onAdminPath = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
  const mustResolveTenant = onAdminPath && !!user && (loading || profile?.role === "store_manager");

  // Hold back the admin tree while auth/tenant scope is resolving. This avoids
  // the previous first-render leak where CloudSyncProvider loaded main-store
  // local data before a Store Manager tenant was attached.
  if ((hostAttach.resolving && !tenantId) || (mustResolveTenant && (loading || resolving || (profile?.role === "store_manager" && !tenantId)))) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading your store…</p>
      </div>
    );
  }
  return (
    <>
      <ManagedByPlatformBanner />
      {children}
    </>
  );
}
